// The venue's holiday calendar (spec/13 §13, redesigned 2026-10-07): the collection pattern of the kit
// (`ListCard`, `ListRow`). Each holiday is one readable row — "Diwali · Sun 8 Nov · Closed", "Christmas Eve · Thu
// 24 Dec · 12 pm – 10 pm" — sorted by date, a past one quiet and marked Past; adding or editing one is a small
// centred dialog (its name, its date, and that day: closed, regular hours or special hours). Empty, the card says
// what holidays are for and offers India's national holidays as quick picks when the venue keeps India's time.
//
// The value is the venue record's `holidays`, exactly as the contract has it (`Holiday`: date, name, closed, hours);
// a change waits in the venue profile's draft and is saved with its one Save and `If-Match`, as before.

import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, CloseIcon, Dialog, Input, ListCard, ListRow, SegmentedControl, TextField } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { clockText } from '../settings/hours';
import type { ControlProps } from '../settings/types';

interface Interval {
  readonly opens: string;
  readonly closes: string;
}

export interface HolidayValue {
  readonly date: string;
  readonly name: string;
  readonly closed: boolean;
  readonly hours: readonly Interval[];
}

type DayKind = 'closed' | 'regular' | 'special';

const MAX_INTERVALS = 4;
const TIME = /^\d{2}:\d{2}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

const holidaysOf = (value: unknown): HolidayValue[] =>
  Array.isArray(value)
    ? value.filter((item): item is HolidayValue => typeof item === 'object' && item !== null && typeof (item as HolidayValue).date === 'string')
    : [];

/** India's national holidays on fixed dates, offered as quick picks (the festivals move each year, so they are typed). */
export const NATIONAL_HOLIDAYS = [
  { id: 'newYear', monthDay: '01-01' },
  { id: 'republicDay', monthDay: '01-26' },
  { id: 'independenceDay', monthDay: '08-15' },
  { id: 'gandhiJayanti', monthDay: '10-02' },
  { id: 'christmas', monthDay: '12-25' },
] as const;

const INDIA_TIMEZONES = ['Asia/Kolkata', 'Asia/Calcutta'];

/** Today, as the venue's calendar date would be read on this computer: `YYYY-MM-DD`. */
function today(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** The next time a fixed date comes round, from today. */
export function nextOccurrence(monthDay: string, from: string): string {
  const year = Number(from.slice(0, 4));
  const thisYear = `${year}-${monthDay}`;
  return thisYear >= from ? thisYear : `${year + 1}-${monthDay}`;
}

/** "2026-11-08" → "Sun 8 Nov" (and the year when it is not this one). */
export function dateText(date: string, language: string, currentYear: string): string {
  if (!DATE.test(date)) return date;
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const when = new Date(Date.UTC(year, month - 1, day));
  return new Intl.DateTimeFormat(language, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(String(year) === currentYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  }).format(when);
}

/** A holiday's hours in words: Closed, Regular hours, or "12 pm – 10 pm". */
export function holidayHoursText(holiday: HolidayValue, t: (key: string, options?: Record<string, unknown>) => string, language: string): string {
  if (holiday.closed) return t('settings.holidays.closed');
  if (holiday.hours.length === 0) return t('settings.holidays.regular');
  return holiday.hours
    .map((interval) =>
      interval.opens === interval.closes
        ? t('settings.hours.open24')
        : t('settings.hours.summary.range', { from: clockText(interval.opens, language), to: clockText(interval.closes, language) }),
    )
    .join(t('settings.hours.summary.and'));
}

export function HolidaysControl(props: ControlProps): ReactNode {
  const { t } = useTranslation('console');
  const { locale } = useVenueFormatter();
  const list = holidaysOf(props.value);
  const now = today();
  const year = now.slice(0, 4);
  const [editing, setEditing] = useState<{ readonly index: number | null; readonly start?: HolidayValue } | null>(null);
  const sorted = list.map((holiday, index) => ({ holiday, index })).sort((a, b) => a.holiday.date.localeCompare(b.holiday.date));
  const thisYear = list.filter((holiday) => holiday.date.startsWith(year)).length;
  const past = list.filter((holiday) => holiday.date < now).length;
  const summary = [
    thisYear === 0 ? t('settings.holidays.summaryNone') : t('settings.holidays.summary', { count: thisYear }),
    past === 0 ? '' : t('settings.holidays.summaryPast', { count: past }),
  ]
    .filter(Boolean)
    .join(' · ');
  const tf = (key: string, options?: Record<string, unknown>) => t(key, options ?? {});

  const save = (holiday: HolidayValue) => {
    if (editing === null) return;
    const next = editing.index === null ? [...list, holiday] : list.map((existing, at) => (at === editing.index ? holiday : existing));
    // Kept by date: the calendar reads in order, and the stored list is the same set either way.
    props.onChange([...next].sort((a, b) => a.date.localeCompare(b.date)).map((one) => ({ ...one, hours: one.hours.map((interval) => ({ ...interval })) })));
    setEditing(null);
  };

  const quickPicks =
    props.context.timezone !== undefined && INDIA_TIMEZONES.includes(props.context.timezone)
      ? NATIONAL_HOLIDAYS.map((holiday) => ({ ...holiday, date: nextOccurrence(holiday.monthDay, now) }))
          .filter((holiday) => !list.some((existing) => existing.date === holiday.date))
          .sort((a, b) => a.date.localeCompare(b.date))
      : [];

  const addButton = props.disabled ? undefined : (
    <Button size="sm" variant="secondary" onClick={() => setEditing({ index: null })}>
      {t('settings.holidays.add')}
    </Button>
  );

  return (
    <div className="console-holidays">
      <ListCard
        className="console-list-card--embedded"
        title={props.label}
        summary={summary}
        count={list.length}
        listLabel={props.label}
        emptyText={t('settings.holidays.emptyText')}
        {...(addButton === undefined ? {} : { action: addButton })}
        emptyExtra={
          quickPicks.length === 0 || props.disabled ? undefined : (
            <div className="console-holidays__quick" role="group" aria-label={t('settings.holidays.quickTitle')}>
              <span className="console-muted">{t('settings.holidays.quickTitle')}</span>
              {quickPicks.map((holiday) => {
                const name = t(`settings.holidays.national.${holiday.id}`);
                const when = dateText(holiday.date, locale, year);
                return (
                  <button
                    key={holiday.id}
                    type="button"
                    className="clav-chip clav-chip--toggle"
                    aria-label={t('settings.holidays.addQuick', { name, date: when })}
                    onClick={() => props.onChange([...list, { date: holiday.date, name, closed: true, hours: [] }].sort((a, b) => a.date.localeCompare(b.date)))}
                  >
                    <span aria-hidden="true">+</span> {t('settings.holidays.quick', { name, date: when })}
                  </button>
                );
              })}
            </div>
          )
        }
      >
        {sorted.map(({ holiday, index }) => {
          const isPast = holiday.date < now;
          return (
            <ListRow
              key={`${holiday.date}:${index}`}
              title={holiday.name}
              meta={[dateText(holiday.date, locale, year), holidayHoursText(holiday, tf, locale)].join(' · ')}
              muted={isPast}
              {...(isPast ? { badge: <Badge tone="neutral">{t('settings.holidays.past')}</Badge> } : {})}
              {...(props.disabled
                ? {}
                : {
                    actions: (
                      <>
                        <Button size="sm" variant="ghost" aria-label={t('settings.holidays.edit', { name: holiday.name })} onClick={() => setEditing({ index, start: holiday })}>
                          {t('settings.holidays.editShort')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<CloseIcon />}
                          label={t('settings.holidays.remove', { name: holiday.name })}
                          onClick={() => props.onChange(list.filter((_, at) => at !== index))}
                        />
                      </>
                    ),
                  })}
            >
              {props.errorAt([...props.path, index]) === undefined ? null : <p className="clav-field__error">{props.errorAt([...props.path, index])}</p>}
            </ListRow>
          );
        })}
      </ListCard>
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        size="sm"
        dirty="auto"
        title={editing?.start === undefined ? t('settings.holidays.addTitle') : t('settings.holidays.editTitle', { name: editing.start.name })}
        description={t('settings.holidays.saveHint')}
      >
        {editing === null ? null : (
          <HolidayForm
            start={editing.start}
            taken={list.filter((_, at) => at !== editing.index).map((holiday) => holiday.date)}
            onCancel={() => setEditing(null)}
            onSave={save}
          />
        )}
      </Dialog>
    </div>
  );
}

function HolidayForm({
  start,
  taken,
  onCancel,
  onSave,
}: {
  readonly start: HolidayValue | undefined;
  readonly taken: readonly string[];
  readonly onCancel: () => void;
  readonly onSave: (holiday: HolidayValue) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const [name, setName] = useState(start?.name ?? '');
  const [date, setDate] = useState(start?.date ?? '');
  const [kind, setKind] = useState<DayKind>(start === undefined ? 'closed' : start.closed ? 'closed' : start.hours.length === 0 ? 'regular' : 'special');
  const [hours, setHours] = useState<Interval[]>(start?.hours.length ? start.hours.map((interval) => ({ ...interval })) : [{ opens: '12:00', closes: '22:00' }]);
  const [errors, setErrors] = useState<{ name?: string; date?: string; hours?: string }>({});

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next: typeof errors = {};
    if (name.trim() === '') next.name = t('settings.holidays.nameRequired');
    if (!DATE.test(date)) next.date = t('settings.holidays.dateRequired');
    else if (taken.includes(date)) next.date = t('settings.holidays.dateTaken');
    if (kind === 'special' && (hours.length === 0 || hours.some((interval) => !TIME.test(interval.opens) || !TIME.test(interval.closes)))) {
      next.hours = t('settings.holidays.hoursRequired');
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSave({ date, name: name.trim(), closed: kind === 'closed', hours: kind === 'special' ? hours : [] });
  };

  return (
    <form className="console-form console-holiday-form" noValidate onSubmit={submit} aria-label={t('settings.holidays.addTitle')}>
      <TextField label={t('settings.holidays.name')} placeholder={t('settings.holidays.namePlaceholder')} required maxLength={80} value={name} onChange={(event) => setName(event.target.value)} error={errors.name} />
      <TextField label={t('settings.holidays.date')} type="date" required value={date} onChange={(event) => setDate(event.target.value)} error={errors.date} />
      <div className="console-field-block">
        <span className="clav-field__label" aria-hidden="true">
          {t('settings.holidays.hoursKind')}
        </span>
        <SegmentedControl<DayKind>
          label={t('settings.holidays.hoursKindLabel')}
          value={kind}
          onValueChange={setKind}
          options={[
            { value: 'closed', label: t('settings.holidays.closed') },
            { value: 'regular', label: t('settings.holidays.regular') },
            { value: 'special', label: t('settings.holidays.special') },
          ]}
        />
      </div>
      {kind === 'special' ? (
        <div className="console-ranges">
          {hours.map((interval, index) => (
            <div className="console-range" key={index}>
              <Input
                type="time"
                numeric
                aria-label={`${t('venueProfile.label.opens')}${hours.length > 1 ? t('settings.hours.rangeNumber', { n: index + 1 }) : ''}`}
                value={interval.opens}
                onChange={(event) => setHours(hours.map((one, at) => (at === index ? { ...one, opens: event.target.value } : one)))}
              />
              <span className="console-range__arrow" aria-hidden="true">
                –
              </span>
              <Input
                type="time"
                numeric
                aria-label={`${t('venueProfile.label.closes')}${hours.length > 1 ? t('settings.hours.rangeNumber', { n: index + 1 }) : ''}`}
                value={interval.closes}
                onChange={(event) => setHours(hours.map((one, at) => (at === index ? { ...one, closes: event.target.value } : one)))}
              />
              {TIME.test(interval.opens) && TIME.test(interval.closes) && interval.closes < interval.opens ? (
                <span className="console-range__note">{t('settings.hours.nextDay')}</span>
              ) : null}
              {hours.length > 1 ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<CloseIcon />}
                  label={t('settings.hours.removeRange', { row: name || t('settings.holidays.title'), n: index + 1 })}
                  onClick={() => setHours(hours.filter((_, at) => at !== index))}
                />
              ) : null}
            </div>
          ))}
          {hours.length < MAX_INTERVALS ? (
            <div>
              <Button size="sm" variant="ghost" onClick={() => setHours([...hours, { opens: '', closes: '' }])}>
                {t('settings.hours.anotherRange')}
              </Button>
            </div>
          ) : null}
          {errors.hours === undefined ? null : <p className="clav-field__error">{errors.hours}</p>}
        </div>
      ) : null}
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('settings.holidays.cancel')}
        </Button>
        <Button type="submit" variant="primary">
          {t('settings.holidays.save')}
        </Button>
      </div>
    </form>
  );
}
